package com.nonopn.skydome.common.app.navigation.scaffold

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material.Divider
import androidx.compose.material.Icon
import androidx.compose.material.IconButton
import androidx.compose.material.DropdownMenu
import androidx.compose.material.DropdownMenuItem
import androidx.compose.material.Surface
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.ArrowDropDown
import androidx.compose.material.icons.filled.Home
import androidx.compose.material.icons.filled.Star
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.setValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nonopn.icons.FixedGps
import com.nonopn.icons.Folder
import com.nonopn.icons.ManageAccounts
import com.nonopn.icons.MapLocation
import com.nonopn.skydome.common.app.home.LocalApp
import com.nonopn.skydome.common.app.home.LocalMenuState
import com.nonopn.skydome.common.app.navigation.RouterActivity
import com.nonopn.skydome.common.app.navigation.RouterDashboard
import com.nonopn.skydome.common.app.navigation.RouterDocuments
import com.nonopn.skydome.common.app.navigation.RouterEstimatesAdmin
import com.nonopn.skydome.common.app.navigation.RouterMapAdmin
import com.nonopn.skydome.common.app.navigation.RouterPromotionCodesAdmin
import com.nonopn.skydome.common.app.navigation.RouterProbes
import com.nonopn.skydome.common.app.navigation.RouterProjects
import com.nonopn.skydome.common.app.navigation.RouterSettings
import com.nonopn.skywatcher.common.app.res.Res
import com.nonopn.skywatcher.common.app.res.menu_admin_estimate_list
import com.nonopn.skywatcher.common.app.res.menu_admin_mode_enter
import com.nonopn.skywatcher.common.app.res.menu_admin_mode_exit
import com.nonopn.skywatcher.common.app.res.menu_admin_promo_codes
import com.nonopn.skywatcher.common.app.res.menu_sr_account
import com.nonopn.skywatcher.common.app.res.menu_sr_activity
import com.nonopn.skywatcher.common.app.res.menu_sr_dashboard
import com.nonopn.skywatcher.common.app.res.menu_sr_documents
import com.nonopn.skywatcher.common.app.res.menu_sr_probes
import com.nonopn.skywatcher.common.app.res.menu_sr_projects
import com.nonopn.skywatcher.common.app.res.strike_radar_logo
import com.nonopn.skywatcher.design.appbar.AppBarState
import com.nonopn.skywatcher.design.theme.AppColor
import com.nonopn.skywatcher.design.utils.LocalWindow
import com.nonopn.skywatcher.design.utils.localized
import com.nonopn.skywatcher.design.widgets.TopAppBarExtended
import eu.codlab.compose.widgets.TextNormal
import eu.codlab.compose.widgets.spacers.TopSpacer
import eu.codlab.navigation.LocalNavigator
import eu.codlab.navigation.NavigateTo
import eu.codlab.navigation.RouteParameterTo
import eu.codlab.navigation.RouterNoParameters
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource

@Composable
fun TopBarWrapper(disableMenuInBigScreen: Boolean = true) {
    val scaffold = LocalMenuState.current
    val appModel = LocalApp.current
    val navigator = LocalNavigator.current

    val scope = rememberCoroutineScope()

    val state by appModel.states.collectAsState()
    val navigatorState by navigator.states.collectAsState()

    val isScreenExpanded = LocalWindow.current.isScreenExpanded()

    if (state.appBarState is AppBarState.Hidden) {
        return
    }

    if (isScreenExpanded) {
        // Tablet: logo + nav items + flags on a single row
        Surface(elevation = 8.dp) {
            TabletTopBar(
                currentRoute = state.currentRoute,
                onNavigate = { navigateTo -> appModel.show(navigateTo) }
            )
        }
    } else {
        // Phone: burger only — navigation via drawer
        Surface(elevation = 8.dp) {
            TopAppBarExtended(
                title = state.appBarState.showTitle(),
                topSpacer = true,
                canGoBack = navigatorState.canGoBack,
                isScreenExpanded = false,
                appModel = appModel,
                painter = painterResource(Res.drawable.strike_radar_logo)
            ) {
                if (navigatorState.canGoBack) {
                    navigator.goBack()
                    return@TopAppBarExtended
                }

                scope.launch {
                    scaffold?.drawerState?.let {
                        if (it.isOpen) {
                            it.close()
                        } else {
                            it.open()
                        }
                    }
                }
            }
        }
    }
}

// ─── Tablet single-row top bar ────────────────────────────────────────────────

@Composable
private fun TabletTopBar(
    currentRoute: RouteParameterTo?,
    onNavigate: (NavigateTo) -> Unit
) {
    data class NavEntry(
        val router: RouterNoParameters<*>?,
        val label: String,
        val icon: ImageVector,
        // Entries without a router (administrator mode switch) run this action instead
        val action: (() -> Unit)? = null
    )

    val tabletAppModel = LocalApp.current
    val tabletState by tabletAppModel.states.collectAsState()
    val hasAdminApi = tabletState.apiProvider?.apiAdmin != null

    val navItems = if (tabletState.isAdminMode && hasAdminApi) {
        // Administrator mode: dashboard, estimates list, keraunic map and the way back
        listOf(
            NavEntry(RouterDashboard, Res.string.menu_sr_dashboard.localized(), Icons.Filled.Home),
            NavEntry(RouterEstimatesAdmin, Res.string.menu_admin_estimate_list.localized(), Icons.Rounded.FixedGps),
            NavEntry(RouterMapAdmin, Res.string.menu_sr_activity.localized(), Icons.Rounded.MapLocation),
            NavEntry(RouterPromotionCodesAdmin, Res.string.menu_admin_promo_codes.localized(), Icons.Filled.Star),
            NavEntry(
                router = null,
                label = Res.string.menu_admin_mode_exit.localized(),
                icon = Icons.Outlined.ManageAccounts,
                action = { tabletAppModel.exitAdminMode() }
            )
        )
    } else {
        val baseNavItems = listOf(
            NavEntry(RouterDashboard, Res.string.menu_sr_dashboard.localized(), Icons.Filled.Home),
            NavEntry(RouterActivity, Res.string.menu_sr_activity.localized(), Icons.Rounded.MapLocation),
            NavEntry(RouterProjects, Res.string.menu_sr_projects.localized(), Icons.Rounded.Folder),
            NavEntry(RouterDocuments, Res.string.menu_sr_documents.localized(), Icons.Rounded.Folder),
            NavEntry(RouterProbes, Res.string.menu_sr_probes.localized(), Icons.Rounded.FixedGps),
            NavEntry(RouterSettings, Res.string.menu_sr_account.localized(), Icons.Outlined.ManageAccounts)
        )

        // Admin accounts also get the switch to the administrator mode
        if (hasAdminApi) {
            baseNavItems + NavEntry(
                router = null,
                label = Res.string.menu_admin_mode_enter.localized(),
                icon = Icons.Outlined.ManageAccounts,
                action = { tabletAppModel.enterAdminMode() }
            )
        } else {
            baseNavItems
        }
    }

    val languages = listOf(
        "🇫🇷" to "fr",
        "🇬🇧" to "en",
        "🇪🇸" to "es",
        "🇧🇷" to "pt"
    )
    val selectedLang = tabletState.appLanguage

    val tabletNavigator = LocalNavigator.current
    val tabletNavState by tabletNavigator.states.collectAsState()

    Column(modifier = Modifier.fillMaxWidth().background(AppColor.White)) {
        TopSpacer(AppColor.White)

        Row(
            modifier = Modifier
                .fillMaxWidth()
                .height(72.dp)
                .padding(horizontal = 12.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            // Back button (visible only when navigation stack has history)
            if (tabletNavState.canGoBack) {
                IconButton(onClick = { tabletNavigator.goBack() }) {
                    Icon(
                        modifier = Modifier.size(24.dp),
                        imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                        contentDescription = null,
                        tint = AppColor.StrikeSidebar
                    )
                }
            }

            // Logo
            Image(
                modifier = Modifier
                    .height(73.dp)
                    .padding(end = 16.dp),
                painter = painterResource(Res.drawable.strike_radar_logo),
                contentDescription = "Strike Radar",
                contentScale = ContentScale.Fit
            )

            // Navigation items — weighted so long labels wrap to 2 lines on narrow tablets
            navItems.forEach { entry ->
                val isActive = entry.router?.isCurrentRoute(currentRoute) ?: false
                val tintColor = if (isActive) AppColor.StrikePrimary else AppColor.StrikeSidebar.copy(alpha = 0.55f)

                Column(
                    modifier = Modifier
                        .weight(1f)
                        .clickable(
                            indication = null,
                            interactionSource = remember { MutableInteractionSource() }
                        ) {
                            val action = entry.action
                            if (action != null) {
                                action()
                            } else {
                                entry.router?.let { onNavigate(it.navigateTo()) }
                            }
                        }
                        .padding(horizontal = 2.dp, vertical = 8.dp),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    Image(
                        modifier = Modifier.size(20.dp),
                        imageVector = entry.icon,
                        contentDescription = null,
                        colorFilter = ColorFilter.tint(tintColor)
                    )
                    Spacer(Modifier.height(2.dp))
                    TextNormal(
                        text = entry.label,
                        fontSize = 11.sp,
                        color = tintColor,
                        fontWeight = if (isActive) FontWeight.Bold else FontWeight.Normal,
                        maxLines = 2,
                        textAlign = TextAlign.Center
                    )
                }
            }

            // Language dropdown with flags
            var langExpanded by remember { mutableStateOf(false) }
            val selectedFlag = languages.first { it.second == selectedLang }.first

            Box {
                Row(
                    modifier = Modifier
                        .clickable(
                            indication = null,
                            interactionSource = remember { MutableInteractionSource() }
                        ) { langExpanded = true }
                        .padding(horizontal = 8.dp, vertical = 4.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    TextNormal(
                        text = selectedFlag,
                        fontSize = 20.sp
                    )
                    Spacer(modifier = Modifier.width(4.dp))
                    Image(
                        modifier = Modifier.size(18.dp),
                        imageVector = Icons.Filled.ArrowDropDown,
                        contentDescription = null,
                        colorFilter = ColorFilter.tint(AppColor.StrikeSidebar)
                    )
                }

                DropdownMenu(
                    expanded = langExpanded,
                    onDismissRequest = { langExpanded = false }
                ) {
                    languages.forEach { (flag, code) ->
                        DropdownMenuItem(onClick = {
                            tabletAppModel.setLanguage(code)
                            langExpanded = false
                        }) {
                            TextNormal(
                                text = flag,
                                fontSize = 20.sp
                            )
                        }
                    }
                }
            }
        }

        Divider(
            modifier = Modifier.fillMaxWidth(),
            color = AppColor.StrikePrimary.copy(alpha = 0.12f),
            thickness = 1.dp
        )
    }
}
